import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import Currency from '@prisjakt/ui-currency';
import { Checkbox } from '@prisjakt/ui-inputs';
import Tooltip from '@prisjakt/ui-tooltip';
import ArrowDown from 'mdi-react/ArrowDownDropCircleIcon';
import ArrowUp from 'mdi-react/ArrowRightDropCircleIcon';
import ChartLine from 'mdi-react/ChartLineIcon';
import Spinner from '@prisjakt/ui-spinner';

import { productPriceClick } from '../../../product/actions/product';

import './index.scss';

const styles = bemGenerator('product-price-history-table', [
  'table',
  'expanded',
  'description',
  'plot',
  'store',
  'date',
  'name',
  'name-row',
  'price',
  'price-change',
  'external',
  'price-row',
  'expand-column',
  'expand-icon',
  'loading',
]);

const SATURATION = 50;
const LIGHTNESS = 65;
const COLOR_CHUNKS = 4;

const getHSL = (function(startIndex) {
  let index = startIndex;

  return () => {
    const step = 360 / COLOR_CHUNKS;
    const offset = step / (Math.floor(index / COLOR_CHUNKS) + 1);
    const indexInChunk = index % COLOR_CHUNKS;

    index += 1;

    return `hsl(${offset +
      indexInChunk * step}, ${SATURATION}%, ${LIGHTNESS}%)`;
  };
})(0);

const renderLoading = () => (
  <tr key="loading" className={styles.loading}>
    <td />
    <td />
    <td>
      <div className={styles.loading.withModifier({ spinner: true })}>
        <Spinner size={28} isActive />
      </div>
    </td>
    <td />
    <td className={styles.name} />
    <td className={styles.external} />
  </tr>
);

class PriceHistoryTable extends React.PureComponent {
  renderEmptyRow() {
    const { t } = this.context;
    return (
      <tr>
        <td />
        <td />
        <td>{t('common', 'price.no-prices')}</td>
        <td />
        <td className={styles.name} />
        <td className={styles.external} />
      </tr>
    );
  }

  renderGoToStoreLink(price) {
    const { t } = this.context;

    if (!price.externalUri) {
      return null;
    }
    return (
      <a
        onClick={e => {
          if (price.externalUri && !e.defaultPrevented) {
            this.props.productPriceClick(this.props.productId, price, true, '');
          }
        }}
        href={price.externalUri}
        // eslint-disable-next-line react/jsx-no-target-blank
        target="_blank"
        rel="nofollow noopener"
      >
        {t('product', 'store.link')}
      </a>
    );
  }

  renderSinglePriceHistory(price) {
    const { expandedPrices, priceHistory } = this.props;
    const expandedPrice = expandedPrices[price.id];

    if (!expandedPrice || !priceHistory[price.id]) {
      return null;
    }

    const currentPriceHistory = priceHistory[price.id];

    if (currentPriceHistory.fetching) {
      return renderLoading();
    }

    if (currentPriceHistory.prices.length === 0) {
      return this.renderEmptyRow();
    }

    return this.renderExpandedRows(price, currentPriceHistory.prices);
  }

  renderExpandedRows(price, prices) {
    return (
      <>
        <tr className={styles.expanded}>
          <td colSpan={3}>{price.name}</td>
          <td
            className={styles.external.withModifier({
              expanded: true,
            })}
          >
            {this.renderGoToStoreLink(price)}
          </td>
        </tr>
        {prices.map(p =>
          this.renderExpandedRow(
            p,
            `price-${price.id}-${price.store.id}-${p.latestPriceChange.time}-${p.price}`
          )
        )}
      </>
    );
  }

  renderExpandedRow(price, key = `price-${price.price}`) {
    return this.renderRow(price, key, true);
  }

  renderRow(price, key = price.id, expanded = false) {
    const { expandedPrices, selectedPrices, onTogglePriceGraph } = this.props;
    return (
      <tr
        className={styles['price-row']}
        key={key}
        onClick={event =>
          !expanded &&
          !price.newPrice &&
          event.target.tagName !== 'A' &&
          this.toggle(price)
        }
        data-test="PriceHistoryTableRow"
      >
        <td className={styles.plot}>
          {!price.newPrice && !expanded && (
            <Checkbox
              type="checkbox"
              checked={Boolean(selectedPrices[price.id])}
              onChange={() => onTogglePriceGraph(price.id, getHSL())}
              name={key}
              data-test="PriceHistoryTableRowGraphToggle"
            />
          )}
        </td>
        <td className={styles.store}>
          <div className={styles['expand-column']}>
            {!expanded && !price.newPrice && (
              <button type="button" className={styles['expand-icon']}>
                {expandedPrices[price.id] ? (
                  <ArrowDown height={12} />
                ) : (
                  <ArrowUp height={12} />
                )}
              </button>
            )}
            {price.store && price.store.name}
          </div>
        </td>
        <td className={styles.date}>{price.latestPriceChange.time}</td>
        <td className={styles.price}>
          <Currency code={price.currency} price={price.price} />
        </td>
        <td
          className={styles['price-change'].withModifier({
            positive: price.latestPriceChange.amount > 0,
            negative: price.latestPriceChange.amount < 0,
          })}
        >
          {!price.newPrice && (
            <Currency
              code={price.currency}
              price={price.latestPriceChange.amount}
              asDiff
            />
          )}
        </td>
        <td className={styles.name}>
          <div title={price.name}>{price.name}</div>
        </td>
        <td className={styles.external}>{this.renderGoToStoreLink(price)}</td>
      </tr>
    );
  }

  renderHeader() {
    const { t } = this.context;
    return (
      <thead>
        <tr>
          <th className={styles.plot}>
            <ChartLine />
            <Tooltip>{t('product', 'price-history.add-to-graph')}</Tooltip>
          </th>
          <th className={styles.store}>
            {t('product', 'price-history.store-header')}
          </th>
          <th className={styles.date}>
            {t('product', 'price-history.date-header')}
          </th>
          <th className={styles.price}>
            {t('product', 'price-history.price-header')}
          </th>
          <th className={styles.price}>
            {t('product', 'price-history.price-change-header')}
          </th>
          <th className={styles.name}>
            {t('product', 'price-history.product-description-header')}
          </th>
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <th className={styles.external} />
        </tr>
      </thead>
    );
  }

  toggle(price) {
    this.props.onToggleExpandPrice(price.id);
  }

  render() {
    const { t } = this.context;

    if (!this.props.prices.length) {
      return null;
    }

    return (
      <div className={styles.block} data-test="PriceHistoryTable">
        <p className={styles.description}>
          {t('product', 'price-history.description-detail')}
        </p>
        <table className={styles.table}>
          {this.renderHeader()}
          <tbody>
            {this.props.prices.map(price => (
              <Fragment key={price.id}>
                {this.renderRow(price)}
                {this.renderSinglePriceHistory(price)}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
}

PriceHistoryTable.defaultProps = {
  productId: undefined,
  prices: undefined,
  expandedPrices: {},
  selectedPrices: {},
  priceHistory: {},
};

const { func, number, array, object } = PropTypes;

PriceHistoryTable.propTypes = {
  productId: number,
  prices: array,
  productPriceClick: func.isRequired,
  onTogglePriceGraph: func.isRequired,
  onToggleExpandPrice: func.isRequired,
  expandedPrices: object,
  selectedPrices: object,
  priceHistory: object,
};

PriceHistoryTable.contextType = AppContext;

export default connect(null, { productPriceClick })(PriceHistoryTable);
